// @flow
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSetState, useDebounce } from 'react-use';
import { NetworkStatus } from 'apollo-client';
import { debounce, orderBy, uniqBy } from 'lodash';
import { token } from '@atlaskit/tokens';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
    SelectFilter,
    SearchFilter,
    RefinementBarProvider,
    RefinementBarConsumer,
    RefinementBarUI,
    AsyncSelectFilter,
    AvatarAsyncSelectFilter,
} from '@atlaskit/refinement-bar';
import { type OptionsType } from '@atlaskit/select';
import { compose, withApollo } from 'react-apollo';
import Tick from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Loading from '../components/virtual-components/Loading';
import VirtualComponentReleaseEdit from '../components/virtual-component-releases/VirtualComponentReleaseEdit';
import { analytics } from '../services/analytics';
import { loadVirtualComponentsAndParseToOptions } from '../components/select/VirtualComponentSelect';
import { VIRTUAL_COMPONENT_RELEASES_QUERY_PAGINATED } from '../graphql/queries';
import { UPDATE_VIRTUAL_COMPONENT_RELEASE, DELETE_VIRTUAL_COMPONENT_RELEASE } from '../graphql/mutations';
import { loadProjectsOptions } from '../components/select/ProjectSelect';
import ProjectVersionSelect from '../components/refinement-bar/fields/ProjectVersionSelect';
import VirtualComponentVersionsTable from '../components/virtual-component-releases/VirtualComponentVersionsTable';
import { withFlagContext, Context as FlagContext } from '../contexts/flagContext';
import DeleteVirtualComponentVersionModal from './DeleteVirtualComponentVersionModal';
import PaginationContainer from '../components/pagination/PaginationContainer';

const Container = styled.div`
    width: 100%;
`;

const SearchRow = styled.div`
    display: flex;
    margin: 0 0 16px;
`;

const RefinementBarWrapper = styled.div`
    button {
        background-color: ${token('color.background.neutral')};
        color: ${token('color.text')};
    }
    button:hover {
        background-color: ${token('color.background.neutral.hovered')} !important;
        color: ${token('color.text')} !important;
    }
    button:active {
        background-color: ${token('color.background.neutral.hovered')} !important;
        color: ${token('color.text')} !important;
    }
    input {
        background-color: ${token('color.background.neutral')};
        color: ${token('color.text')};
    }
    input:hover {
        background-color: ${token('color.background.neutral.hovered')};
    }
    input:active {
        background-color: ${token('color.background.neutral.hovered')};
    }
    input:focus {
        background-color: ${token('color.background.neutral.hovered')};
    }
`;

const statuses = [
    { value: 1, label: 'Released' },
    { value: 0, label: 'Unreleased' },
];

const irremovableKeys = ['search', 'components', 'projects', 'versions', 'status'];

const VirtualComponentVersions = ({
    putContentToRightSidePanel,
    hideRightSidePanel,
    refetchKey,
    openCreateComponentReleaseModal,
    client,
    onComponentReleasesFilterChange,
}) => {
    const { pushFlag } = useContext(FlagContext);
    const [state, setState] = useSetState({
        filters: {
            status: [statuses[1]],
            projects: [],
            components: [],
            versions: [],
        },
        confirmComponentVersionDelete: null,
        selectedComponents: [],
        selectedProjects: [],
        pagination: { pageSize: 15, page: 0 },
        sorting: {
            property: 'name',
            direction: 'asc',
        },
    });
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(state.filters.search);
    const [, cancel] = useDebounce(() => setDebouncedSearchTerm(state.filters.search), 500, [state.filters.search]);
    const filters = useMemo(
        () => ({
            name: debouncedSearchTerm,
            components: state.filters.components?.map((c) => c.value),
            projects: state.filters.projects?.map((p) => p.value),
            versions: state.filters.versions?.map((v) => parseInt(v.value, 10)),
            status: state.filters.status?.map((s) => s.value),
        }),
        [debouncedSearchTerm, state.filters],
    );
    const {
        data: virtualComponentVersions,
        refetch,
        networkStatus,
        error,
    } = useQuery(VIRTUAL_COMPONENT_RELEASES_QUERY_PAGINATED, {
        variables: {
            filter: filters,
            pagination: state.pagination,
            sorting: state.sorting,
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const reload = useCallback(() => {
        refetch({
            filter: filters,
            pagination: state.pagination,
            sorting: state.sorting,
            key: Date.now(),
        });
    }, [filters, refetch, state.pagination, state.sorting]);

    useEffect(() => {
        onComponentReleasesFilterChange({
            filter: filters,
            pagination: state.pagination,
            sorting: state.sorting,
        });
    }, [filters, state.pagination, state.sorting, onComponentReleasesFilterChange]);

    const [updateVirtualComponentRelease, { loading: isUpdatingVersion }] = useMutation(
        UPDATE_VIRTUAL_COMPONENT_RELEASE,
    );
    const [deleteComponentVersionMutation, { loading: isDeletingVersion }] = useMutation(
        DELETE_VIRTUAL_COMPONENT_RELEASE,
    );
    const displayComponentVersionContent = useCallback(
        (version) => {
            putContentToRightSidePanel(
                <VirtualComponentReleaseEdit
                    onRefetchRequest={reload}
                    addFlag={pushFlag}
                    onClose={hideRightSidePanel}
                    releaseId={version.id}
                />,
                null,
                true,
            );

            analytics.track({
                userId: window.license,
                event: 'accessed_component_version',
                timestamp: new Date(),
                properties: {
                    addon_key: process.env.REACT_APP_KEY,
                },
            });
        },
        [hideRightSidePanel, pushFlag, putContentToRightSidePanel, reload],
    );
    const onReleaseStatusUpdate = useCallback(
        (release) => {
            if (!release.virtualComponent) {
                pushFlag({
                    created: Date.now(),
                    icon: <ErrorIcon primaryColor="#DE350B" />,
                    title: 'An error occurred.',
                    description: "Component version can't be updated. Please assign a component to it.",
                    key: 'error',
                });
                return;
            }
            const input = {
                id: release.id,
                component_id: release.virtualComponent.id,
                name: release.name,
                is_released: !release.isReleased,
                description: release.description,
                linked_versions:
                    release.selectedProjectVersions.map((v) => {
                        return {
                            jiraProjectId: v.jiraProjectId,
                            jiraProjectVersionId: parseInt(v.jiraProjectVersionId, 10),
                        };
                    }) || [],
                start_at: release.startDate,
                end_at: release.releaseDate,
            };
            updateVirtualComponentRelease({
                variables: {
                    input,
                },
            })
                .then(() => {
                    reload();
                    analytics.track({
                        userId: window.license,
                        event: 'released_component_version',
                        timestamp: new Date(),
                        properties: {
                            addon_key: process.env.REACT_APP_KEY,
                            release_status: input.is_released ? 'released' : 'unreleased',
                        },
                    });
                    pushFlag({
                        created: Date.now(),
                        icon: <Tick primaryColor="#00875A" />,
                        title: `Component ${input.is_released ? 'released' : 'unreleased'} successfully.`,
                        key: 'success',
                    });
                })
                .catch(() => {
                    pushFlag({
                        created: Date.now(),
                        icon: <ErrorIcon primaryColor="#DE350B" />,
                        title: 'An error occurred.',
                        description: 'Component version not updated.',
                        key: 'error',
                    });
                });
        },
        [reload, pushFlag, updateVirtualComponentRelease],
    );
    const onFilterChange = (value: Object, action: Object) => {
        setState((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: action.key === 'search' ? prev.pagination.page : 0,
            },
            selectedComponents: orderBy(value?.components ?? [], [({ label }) => label.toLowerCase()], ['asc']),
            selectedProjects: orderBy(value?.projects ?? [], [({ label }) => label.toLowerCase()], ['asc']),
            filters: {
                ...prev.filters,
                [action.key]: action.data,
            },
        }));
    };

    const componentOptionsAsync = useMemo(
        () =>
            debounce((searchText: string, callback: (options: OptionsType[]) => void) => {
                loadVirtualComponentsAndParseToOptions(client, searchText).then((options) => {
                    const filteredSelectedComponents = searchText?.length
                        ? (state.selectedComponents ?? []).filter((item) =>
                              item.label.toLowerCase().includes(searchText.toLowerCase()),
                          )
                        : state.selectedComponents ?? [];
                    return callback(uniqBy([...filteredSelectedComponents, ...(options ?? [])], 'value'));
                });
            }, 300),
        [client, state.selectedComponents],
    );

    const projectOptionsAsync = useMemo(
        () =>
            debounce((searchText: string, callback: (options: OptionsType[]) => void) => {
                loadProjectsOptions(searchText).then((options) => {
                    const filteredSelectedProjects = searchText?.length
                        ? (state.selectedProjects ?? []).filter((item) =>
                              item.label.toLowerCase().includes(searchText.toLowerCase()),
                          )
                        : state.selectedProjects ?? [];
                    return callback(uniqBy([...filteredSelectedProjects, ...(options ?? [])], 'value'));
                });
            }, 300),
        [state.selectedProjects],
    );

    useEffect(() => {
        return () => {
            componentOptionsAsync?.cancel();
            projectOptionsAsync?.cancel();
            cancel();
        };
    }, [componentOptionsAsync, projectOptionsAsync, cancel]);

    useEffect(() => {
        if (refetchKey) {
            reload();
        }
    }, [refetchKey, reload]);

    const fieldConfig = useMemo(
        () => ({
            search: { label: 'Search', type: SearchFilter },
            components: {
                label: 'Components',
                type: AsyncSelectFilter,
                loadOptions: componentOptionsAsync,
                cacheOptions: true,
                defaultOptions: true,
                placeholder: 'Start typing to find more',
            },
            projects: {
                label: 'Projects',
                type: AvatarAsyncSelectFilter,
                loadOptions: projectOptionsAsync,
                cacheOptions: true,
                defaultOptions: true,
                placeholder: 'Start typing to find more',
            },
            versions: {
                label: 'Project versions',
                type: ProjectVersionSelect,
                isOpen: true,
            },
            status: {
                label: 'Status',
                type: SelectFilter,
                options: statuses,
            },
        }),
        [componentOptionsAsync, projectOptionsAsync],
    );

    const onPageChange = useCallback(
        (page, pageSize) => {
            setState((prev) => {
                if (prev.pagination.page !== page || prev.pagination.pageSize !== pageSize) {
                    return { ...prev, pagination: { page, pageSize } };
                }
                return prev;
            });
        },
        [setState],
    );

    const setConfirmComponentVersionDelete = useCallback(
        (componentVersion) =>
            setState({
                confirmComponentVersionDelete: componentVersion?.id ? componentVersion : null,
            }),
        [setState],
    );

    const afterVersionDelete = useCallback(() => {
        setConfirmComponentVersionDelete();
        reload();
    }, [reload, setConfirmComponentVersionDelete]);

    const onSortingChange = useCallback(
        ({ key, sortOrder }) =>
            setState((prev) => ({ ...prev, sorting: { property: key, direction: sortOrder.toLowerCase() } })),
        [setState],
    );

    const hasFilterApplied = useMemo(
        () =>
            state.filters?.search ||
            state.filters?.components?.length ||
            state.filters?.projects?.length ||
            state.filters?.versions?.length ||
            state.filters?.status?.length,
        [
            state.filters?.components?.length,
            state.filters?.projects?.length,
            state.filters?.search,
            state.filters?.status?.length,
            state.filters?.versions?.length,
        ],
    );

    if ([NetworkStatus.loading, NetworkStatus.refetch].includes(networkStatus)) {
        return <Loading />;
    }
    if (error) {
        return `Error! ${error?.message}`;
    }
    return (
        <Container>
            {state.confirmComponentVersionDelete && (
                <DeleteVirtualComponentVersionModal
                    onClose={setConfirmComponentVersionDelete}
                    pushFlag={pushFlag}
                    onComplete={afterVersionDelete}
                    virtualComponentVersion={state.confirmComponentVersionDelete}
                    deleteComponentVersionMutation={deleteComponentVersionMutation}
                    isDeleting={isDeletingVersion}
                />
            )}

            <SearchRow>
                <RefinementBarWrapper>
                    <RefinementBarProvider
                        fieldConfig={fieldConfig}
                        irremovableKeys={irremovableKeys}
                        onChange={onFilterChange}
                        value={state.filters}
                    >
                        <RefinementBarUI />
                        <RefinementBarConsumer>{({ value }) => <div value={value} />}</RefinementBarConsumer>
                    </RefinementBarProvider>
                </RefinementBarWrapper>
            </SearchRow>

            <VirtualComponentVersionsTable
                virtualComponentVersions={virtualComponentVersions?.virtualComponentReleasesPaginated?.rows ?? []}
                openCreateVirtualComponentVersionModal={openCreateComponentReleaseModal}
                editVirtualComponentVersion={displayComponentVersionContent}
                setToDelete={setConfirmComponentVersionDelete}
                setReleaseStatus={onReleaseStatusUpdate}
                hasFilterApplied={hasFilterApplied}
                isLoading={NetworkStatus.setVariables === networkStatus || isDeletingVersion || isUpdatingVersion}
                sorting={state.sorting}
                onSortingChange={onSortingChange}
            />
            {!!virtualComponentVersions?.virtualComponentReleasesPaginated?.count && (
                <PaginationContainer
                    rowsCount={virtualComponentVersions.virtualComponentReleasesPaginated.count}
                    initialPageSize={state.pagination.pageSize}
                    onPageChange={onPageChange}
                />
            )}
        </Container>
    );
};

export default compose(withFlagContext, withApollo)(VirtualComponentVersions);
